import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const packages = [
  {
    id: "visual_single",
    name: "Visuel unique",
    price: "80",
    suffix: "€",
    highlight: false,
    features: [
      "1 visuel sur-mesure",
      "Conçu selon votre marque",
      "Livraison rapide",
      "1 révision incluse",
    ],
  },
  {
    id: "visual_pack_2",
    name: "Pack 2 visuels",
    price: "150",
    suffix: "€",
    highlight: false,
    features: [
      "2 visuels cohérents",
      "Ligne éditoriale maîtrisée",
      "Formats multi-supports",
      "2 révisions incluses",
    ],
  },
  {
    id: "visual_pack_4",
    name: "Pack 4 visuels",
    price: "280",
    suffix: "€",
    highlight: true,
    badge: "Le plus demandé",
    features: [
      "4 visuels premium",
      "Campagne complète",
      "Formats réseaux & print",
      "Révisions illimitées",
    ],
  },
  {
    id: "visual_print",
    name: "Visuels print",
    price: "60",
    suffix: "€",
    prefix: "",
    highlight: false,
    features: [
      "Flyers, affiches, cartes",
      "Fichiers prêts à imprimer",
      "Qualité haute définition",
      "Sur-mesure",
    ],
  },
];

export default function Pricing() {
  const [loadingId, setLoadingId] = useState(null);

  const handleBuy = async (pkg) => {
    try {
      setLoadingId(pkg.id);
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Impossible d'ouvrir le paiement.");
      }
    } catch (e) {
      toast.error("Erreur paiement. Merci de réessayer.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 section-light"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Création de visuels
          </span>
          <h2 className="mt-4 font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A]">
            Des visuels qui{" "}
            <span className="gradient-text font-medium">convertissent</span>
          </h2>
          <p className="mt-6 text-[#0A0A0A]/60 text-lg font-light">
            Visuels réseaux sociaux, packs campagnes, supports print — achat en
            un clic, livraison rapide.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              whileHover={{ y: -6 }}
              data-testid={`pricing-card-${pkg.id}`}
              className={`group relative rounded-3xl p-8 transition-all duration-500 ${
                pkg.highlight
                  ? "bg-gradient-to-b from-[#0A0A0A] to-[#141418] text-white border border-[#E52431]/40 glow-red"
                  : "light-card light-card-hover"
              }`}
            >
              {pkg.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                  <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                    {pkg.badge}
                  </div>
                </div>
              )}

              <div
                className={`absolute top-0 left-8 right-8 h-px ${
                  pkg.highlight
                    ? "bg-gradient-to-r from-transparent via-[#E52431] to-transparent"
                    : "bg-gradient-to-r from-transparent via-[#0A0A0A]/20 to-transparent"
                }`}
              />

              <h3
                className={`font-outfit text-xl font-medium ${
                  pkg.highlight ? "text-white/85" : "text-[#0A0A0A]"
                }`}
              >
                {pkg.name}
              </h3>

              <div className="mt-6 flex items-baseline gap-2">
                {pkg.prefix && (
                  <span
                    className={`text-sm ${
                      pkg.highlight ? "text-white/50" : "text-[#0A0A0A]/50"
                    }`}
                  >
                    {pkg.prefix}
                  </span>
                )}
                <span
                  className={`font-outfit text-5xl md:text-6xl font-light tracking-tight ${
                    pkg.highlight ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {pkg.price}
                </span>
                <span
                  className={`text-2xl ${
                    pkg.highlight ? "text-white/70" : "text-[#0A0A0A]/70"
                  }`}
                >
                  {pkg.suffix}
                </span>
                <span
                  className={`text-[10px] font-semibold tracking-[0.15em] ml-1 ${
                    pkg.highlight ? "text-white/55" : "text-[#0A0A0A]/55"
                  }`}
                >
                  TTC
                </span>
              </div>

              <ul className="mt-8 space-y-3 min-h-[160px]">
                {pkg.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-3 text-sm ${
                      pkg.highlight ? "text-white/75" : "text-[#0A0A0A]/70"
                    }`}
                  >
                    <Check
                      size={16}
                      strokeWidth={2}
                      className={
                        pkg.highlight
                          ? "text-[#E52431] mt-0.5"
                          : "text-[#E52431] mt-0.5"
                      }
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`buy-btn-${pkg.id}`}
                disabled={loadingId === pkg.id}
                onClick={() => handleBuy(pkg)}
                className={`mt-8 w-full rounded-full py-4 font-semibold text-sm uppercase tracking-[0.15em] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 ${
                  pkg.highlight
                    ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                    : "bg-[#0A0A0A] text-white hover:bg-[#E52431]"
                }`}
              >
                {loadingId === pkg.id ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Redirection…
                  </>
                ) : (
                  "Acheter"
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-[#0A0A0A]/50 font-light">
          Chaque visuel est conçu sur mesure selon votre marque et votre
          objectif.
        </p>
        <p className="mt-2 text-center text-xs text-[#0A0A0A]/40 font-light">
          Tous les prix affichés sont en TTC.
        </p>

        <div
          data-testid="clause-visuels"
          className="mt-10 max-w-3xl mx-auto rounded-2xl border border-[#0A0A0A]/10 bg-white px-6 py-5 md:px-7 md:py-6"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#E52431]/10 border border-[#E52431]/20 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#E52431] font-semibold">
                Clause visuels
              </span>
            </div>
            <p className="text-sm text-[#0A0A0A]/70 font-light leading-relaxed">
              Toute commande de visuels réalisée via le site internet inclut
              un maximum de{" "}
              <strong className="text-[#0A0A0A] font-medium">
                3 demandes de modifications
              </strong>
              . Au-delà de ces 3 retours, des frais supplémentaires pourront
              être appliqués selon la nature des modifications demandées.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
